import { useTranslation } from 'react-i18next';
import type { JSX } from '@gov-design-system-ce/components';
import type { FavoriteCode } from '@gov-nx/api';
import {
	CommonSettingKey,
	saveUserSettingQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { useMessageEvents } from '@gov-nx/core/events';
import { PageCode } from '@gov-nx/module/page';
import { useUserStore, useUserStoreFavorites } from '@gov-nx/store';
import { isPageCode } from '../../../../module/page/src/types/pageTypeHelpers';

const favoriteCodeToI18nKeypart = (code: FavoriteCode) =>
	isPageCode(code) ? 'stranka' : 'sluzba';

const blacklistedCodes: FavoriteCode[] = [
	PageCode['podpora'],
	PageCode['uzivatelska-prirucka'],
];

export interface FavoriteToggleCommonProps {
	code: FavoriteCode;
	name?: string;
	size?: JSX.GovButton['size'];
	withLabel?: boolean;
	disabled?: boolean;
	customClass?: string;
}

export const useFavoriteToggle = ({
	code,
	name,
}: FavoriteToggleCommonProps) => {
	const setUserSettingFavorites = useUserStore('setUserSettingFavorites');
	const favorites = useUserStoreFavorites();
	const isFavorite = favorites.includes(code);
	const { axiosInstance } = useAxiosInstance();

	const { t } = useTranslation();
	const { toastMessageSuccess } = useMessageEvents();

	const i18nStaticParts = ['uzivatel', 'oblibene'];
	const i18nLabelVariableParts = [
		favoriteCodeToI18nKeypart(code),
		isFavorite ? 'odebrat' : 'pridat',
	];

	const label = t([...i18nStaticParts, ...i18nLabelVariableParts].join('.'));
	const ariaLabel = t(
		[...i18nStaticParts, 'wcag', ...i18nLabelVariableParts].join('.'),
		{ name }
	);

	const toggleFavorites = (): string[] =>
		isFavorite
			? favorites.filter((item) => item !== code)
			: [...favorites, code];

	const mutation = usePoMutation({
		mutationFn: async (): Promise<boolean> => {
			const toggled = toggleFavorites();
			setUserSettingFavorites(toggled);
			await saveUserSettingQuery(axiosInstance, {
				key: CommonSettingKey.Favorites,
				value: toggled,
			});
			return toggled.includes(code);
		},
		onSuccess: async (updatedIsFavorite) => {
			const i18nToastParts = [
				...i18nStaticParts,
				favoriteCodeToI18nKeypart(code),
				updatedIsFavorite ? 'pridano' : 'odebrano',
			];
			toastMessageSuccess(t(i18nToastParts.join('.')));
		},
	});

	return {
		isEnabled: !blacklistedCodes.includes(code),
		label,
		ariaLabel,
		isFavorite,
		toggle: mutation.mutate,
		isSaving: mutation.isLoading,
	};
};
